import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Drawer, Button, List, Typography, Checkbox, Card, Space, Grid } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  deleteNotification,
  fetchNotifications,
  markAllNotificationAsRead,
  markNotificationAsRead,
} from '../../../../features/notifications/notificationSlice';
import NotificationSkeleton from '../../../Skeleton/NotificationSkeleton';
import { formattedDate } from '../../../../utils/common';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const NotificationCard = ({ notification, onMarkAsRead, onDelete }) => {
  const storedTheme = localStorage.getItem('mainTheme');

  const handleView = () => {
    if (notification?.data?.redirect_url) {
      window.open(notification?.data?.redirect_url, '_blank');
    }
  };

  const handleMarkAsRead = (e) => {
    e.stopPropagation();
    onMarkAsRead(notification?.id);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(notification?.id);
  };

  const getBackgroundColor = () => {
    return storedTheme === 'dark'
      ? notification?.is_read
        ? '#1f1f1f'
        : '#141414'
      : notification?.is_read
        ? '#f7f7f7'
        : '#fff';
  };

  return (
    <Card
      size="small"
      style={{
        marginBottom: 10,
        border: `1px solid ${storedTheme === 'dark' ? '#303030' : '#f0f0f0'}`,
        backgroundColor: getBackgroundColor(),
        transition: 'background-color 0.3s',
      }}
      hoverable
      styles={{ body: { cursor: 'pointer' } }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong={!notification?.is_read} style={{ fontSize: 14 }}>
            {notification?.data?.subject}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formattedDate(notification?.created_at) || ''}
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {notification?.data?.message}
        </Text>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button size="small" type={storedTheme === 'dark' ? 'default' : 'primary'} onClick={handleView}>
            View
          </Button>
          <Space>
            {notification?.is_read ? (
              <Button size="small" icon={<DeleteOutlined />} onClick={handleDelete} />
            ) : (
              <Checkbox checked={notification?.is_read} onChange={handleMarkAsRead}>
                Mark as read
              </Checkbox>
            )}
          </Space>
        </div>
      </Space>
    </Card>
  );
};

const NotificationDrawer = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { notifications, initialLoading, loadingMore, hasMore, limit } = useSelector((state) => state.notification);
  const storedTheme = localStorage.getItem('mainTheme');

  const handleShowMore = () => {
    dispatch(fetchNotifications({ skip: notifications.length, limit }));
  };

  const handleMarkAsRead = (id) => {
    dispatch(markNotificationAsRead(id));
  };

  const handleDelete = (id) => {
    dispatch(deleteNotification(id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationAsRead());
  };

  const unreadNotificationsCount = notifications.filter((n) => !n.is_read).length;

  return (
    <Drawer
      title={<Text strong>Notifications ({unreadNotificationsCount})</Text>}
      placement="right"
      onClose={onClose}
      open={visible}
      width={screens.md ? 400 : '100%'}
      extra={
        unreadNotificationsCount > 0 ? (
          <Button type="link" onClick={handleMarkAllAsRead}>
            Mark all as read
          </Button>
        ) : null
      }
    >
      {initialLoading ? (
        <NotificationSkeleton />
      ) : (
        <>
          <List
            dataSource={notifications}
            renderItem={(item) => (
              <NotificationCard notification={item} onMarkAsRead={handleMarkAsRead} onDelete={handleDelete} />
            )}
          />
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Button onClick={handleShowMore} loading={loadingMore} type="default">
                Show More
              </Button>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default NotificationDrawer;
